import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { apiFetch } from '../utils/api';
import { FinancialValue } from '../components/shared/FinancialValue';
import AppLayout from '../components/AppLayout';
import {
  CheckCircle2,
  Clock,
  DollarSign,
  TrendingUp,
  FileText,
  Plus,
  X,
  Save,
  AlertCircle,
  Star,
  Calendar,
  Loader2,
  Filter,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';

interface ClosedReport {
  id: string;
  claimant_name: string;
  business_type: string;
  event_type: string;
  claim_amount: number;
  status: string;
  created_at: string;
  has_outcome: boolean;
  outcome?: ReportOutcome;
}

interface ReportOutcome {
  id: string;
  report_id: string;
  final_settlement_amount: number;
  settlement_date: string;
  settlement_type: string;
  time_to_resolution_days?: number;
  satisfaction_rating?: number;
  notes?: string;
  settlement_ratio?: number;
  submitted_at: string;
}

interface OutcomeStats {
  total_outcomes: number;
  average_settlement_ratio: number;
  average_time_to_resolution: number;
  average_satisfaction: number;
  settlement_type_breakdown: Record<string, number>;
  industry_breakdown: Record<string, number>;
  event_type_breakdown: Record<string, number>;
}

const settlementTypes = [
  { value: 'negotiated', label: 'Negotiated Settlement' },
  { value: 'mediated', label: 'Mediated Settlement' },
  { value: 'arbitrated', label: 'Arbitrated' },
  { value: 'litigated', label: 'Litigated (Court Decision)' },
  { value: 'denied', label: 'Claim Denied' },
  { value: 'withdrawn', label: 'Claim Withdrawn' },
];

export default function ClosedCasesPage() {
  const { getToken } = useAuth();
  
  const [reports, setReports] = useState<ClosedReport[]>([]);
  const [stats, setStats] = useState<OutcomeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ClosedReport | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    final_settlement_amount: '',
    settlement_date: '',
    settlement_type: 'negotiated',
    time_to_resolution_days: '',
    satisfaction_rating: '',
    notes: ''
  });
  
  // Filter state
  const [filterType, setFilterType] = useState<'all' | 'with_outcome' | 'without_outcome'>('all');
  const [showStats, setShowStats] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch closed reports
      const reportsData = await apiFetch<ClosedReport[]>(
        '/api/v1/outcomes/closed-reports?limit=100',
        { headers }
      );
      setReports(Array.isArray(reportsData) ? reportsData : []);
      
      // Fetch stats
      try {
        const statsData = await apiFetch<OutcomeStats>(
          '/api/v1/outcomes/stats',
          { headers, showErrorToast: false }
        );
        setStats(statsData);
      } catch {
        // Stats may fail if no outcomes yet
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openOutcomeModal = (report: ClosedReport) => {
    setSelectedReport(report);
    if (report.outcome) {
      // Pre-fill form with existing outcome
      setFormData({
        final_settlement_amount: report.outcome.final_settlement_amount.toString(),
        settlement_date: report.outcome.settlement_date,
        settlement_type: report.outcome.settlement_type,
        time_to_resolution_days: report.outcome.time_to_resolution_days?.toString() || '',
        satisfaction_rating: report.outcome.satisfaction_rating?.toString() || '',
        notes: report.outcome.notes || ''
      });
    } else {
      // Reset form
      setFormData({
        final_settlement_amount: '',
        settlement_date: new Date().toISOString().split('T')[0],
        settlement_type: 'negotiated',
        time_to_resolution_days: '',
        satisfaction_rating: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const submitOutcome = async () => {
    if (!selectedReport) return;
    
    try {
      setSubmitting(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        report_id: selectedReport.id,
        final_settlement_amount: parseFloat(formData.final_settlement_amount),
        settlement_date: formData.settlement_date,
        settlement_type: formData.settlement_type,
        time_to_resolution_days: formData.time_to_resolution_days ? parseInt(formData.time_to_resolution_days) : null,
        satisfaction_rating: formData.satisfaction_rating ? parseInt(formData.satisfaction_rating) : null,
        notes: formData.notes || null,
        claim_amount_original: selectedReport.claim_amount
      };
      
      const endpoint = selectedReport.has_outcome 
        ? `/api/v1/outcomes/report/${selectedReport.id}`
        : '/api/v1/outcomes/submit';
      
      const method = selectedReport.has_outcome ? 'PUT' : 'POST';
      
      await apiFetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(payload)
      });
      
      setShowModal(false);
      fetchData(); // Refresh data
      
    } catch (err: any) {
      setError(err.message || 'Failed to submit outcome');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredReports = reports.filter(r => {
    if (filterType === 'with_outcome') return r.has_outcome;
    if (filterType === 'without_outcome') return !r.has_outcome;
    return true;
  });

  const formatPercent = (ratio: number) => {
    return `${(ratio * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]" style={{ background: 'var(--color-bg-base)' }}>
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ background: 'var(--color-bg-base)' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="cs-display">Closed Cases</h1>
        <p className="cs-meta mt-1">
          Track settlement outcomes and build internal benchmarks for better analysis
        </p>
      </div>

      {/* Stats Cards */}
      {stats && stats.total_outcomes > 0 && (
        <div className="mb-8">
          <button
            onClick={() => setShowStats(!showStats)}
            className="cs-label flex items-center gap-2 mb-4"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Benchmark Statistics</span>
            {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
                <div className="flex items-center gap-3">
                  <div className="p-2" style={{ background: 'var(--color-bg-elevated)', borderRadius: 11 }}>
                    <FileText className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                  </div>
                  <div>
                    <p className="cs-figure-lg">{stats.total_outcomes}</p>
                    <p className="cs-meta">Total Outcomes</p>
                  </div>
                </div>
              </div>

              <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
                <div className="flex items-center gap-3">
                  <div className="p-2" style={{ background: 'transparent', borderRadius: 11 }}>
                    <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  </div>
                  <div>
                    <p className="cs-figure-lg">{formatPercent(stats.average_settlement_ratio)}</p>
                    <p className="cs-meta">Avg Settlement Ratio</p>
                  </div>
                </div>
              </div>

              <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
                <div className="flex items-center gap-3">
                  <div className="p-2" style={{ background: 'var(--color-bg-elevated)', borderRadius: 11 }}>
                    <Clock className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                  </div>
                  <div>
                    <p className="cs-figure-lg">{Math.round(stats.average_time_to_resolution)} days</p>
                    <p className="cs-meta">Avg Resolution Time</p>
                  </div>
                </div>
              </div>

              <div className="p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
                <div className="flex items-center gap-3">
                  <div className="p-2" style={{ background: 'var(--color-needs-human-tint)', borderRadius: 11 }}>
                    <Star className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <div>
                    <p className="cs-figure-lg">{stats.average_satisfaction.toFixed(1)}/5</p>
                    <p className="cs-meta">Avg Satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="cs-body rounded-lg"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            {/* F2.85: filter is "Closed" (case workflow ended) — distinct
                from "Completed" (outcome recorded). The two outcome-state
                buckets live underneath. */}
            <option value="all">All Closed Cases ({reports.length})</option>
            <option value="without_outcome">Awaiting Outcome ({reports.filter(r => !r.has_outcome).length})</option>
            <option value="with_outcome">Outcome Submitted ({reports.filter(r => r.has_outcome).length})</option>
          </select>
        </div>
        
        <p className="cs-meta">
          {filteredReports.length} {filteredReports.length === 1 ? 'case' : 'cases'} shown
        </p>
      </div>

      {/* Reports List */}
      {error && (
        <div className="rounded-lg p-4 mb-6 flex items-center gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
          <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
        </div>
      )}

      <div className="overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead style={{ background: 'var(--color-bg-base)' }}>
              <tr>
                <th className="cs-label px-6 py-3 text-left">
                  Claimant
                </th>
                <th className="cs-label px-6 py-3 text-left">
                  Claim Amount
                </th>
                <th className="cs-label px-6 py-3 text-left">
                  Settlement
                </th>
                <th className="cs-label px-6 py-3 text-left">
                  Ratio
                </th>
                <th className="cs-label px-6 py-3 text-left">
                  Status
                </th>
                <th className="cs-label px-6 py-3 text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center" style={{ color: 'var(--color-text-muted)' }}>
                    <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
                    <p className="cs-h3">No completed cases found</p>
                    <p className="cs-meta">Complete some claims to start tracking outcomes</p>
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr
                    key={report.id}
                    style={{ borderTop: '1px solid var(--color-border-faint)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{report.claimant_name}</p>
                        <p className="cs-meta">{report.business_type} • {report.event_type}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="cs-figure">
                        <FinancialValue value={report.claim_amount} />
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.has_outcome && report.outcome ? (
                        <div>
                          <p className="cs-figure" style={{ color: 'var(--color-verified)' }}>
                            <FinancialValue value={report.outcome.final_settlement_amount} />
                          </p>
                          <p className="cs-meta">{report.outcome.settlement_type}</p>
                        </div>
                      ) : (
                        <span className="cs-figure" style={{ color: 'var(--color-text-ghost)' }}>—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.outcome?.settlement_ratio ? (
                        <span
                          className="cs-figure"
                          style={{
                            color:
                              report.outcome.settlement_ratio >= 0.9 ? 'var(--color-verified)' :
                              report.outcome.settlement_ratio >= 0.7 ? 'var(--color-needs-human-text)' :
                              'var(--color-text-muted)'
                          }}
                        >
                          {formatPercent(report.outcome.settlement_ratio)}
                        </span>
                      ) : (
                        <span className="cs-figure" style={{ color: 'var(--color-text-ghost)' }}>—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.has_outcome ? (
                        <span
                          className="cs-chip inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
                          style={{ background: 'transparent', color: 'var(--color-verified)', border: '1px solid rgba(34,197,94,0.25)' }}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Outcome Recorded
                        </span>
                      ) : (
                        <span
                          className="cs-chip inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
                          style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          Awaiting Outcome
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openOutcomeModal(report)}
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition"
                        style={
                          report.has_outcome
                            ? { color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }
                            : { color: 'var(--color-on-brand)', background: 'var(--color-brand)', border: '1px solid var(--color-brand)' }
                        }
                      >
                        {report.has_outcome ? (
                          <>Edit</>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            Add Outcome
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Outcome Modal */}
      {showModal && selectedReport && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              style={{ background: 'rgba(10,10,10,0.75)' }}
              onClick={() => setShowModal(false)}
            />

            <div className="relative transform overflow-hidden text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
              <div className="px-6 py-4" style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="cs-h2">
                      {selectedReport.has_outcome ? 'Edit Outcome' : 'Record Outcome'}
                    </h3>
                    <p className="cs-meta">{selectedReport.claimant_name}</p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{ color: 'var(--color-text-dim)' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Claim Amount Reference */}
                <div className="p-3" style={{ background: 'var(--color-bg-elevated)', borderRadius: 11 }}>
                  <p className="cs-label">Original Claim Amount</p>
                  <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontSize: '1rem' }}>
                    <FinancialValue value={selectedReport.claim_amount} />
                  </p>
                </div>

                {/* Final Settlement Amount */}
                <div>
                  <label className="cs-label block mb-1">
                    Final Settlement Amount *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    <input
                      type="number"
                      value={formData.final_settlement_amount}
                      onChange={(e) => setFormData({ ...formData, final_settlement_amount: e.target.value })}
                      className="cs-body w-full pl-9 pr-4 py-2 rounded-lg"
                      style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                {/* Settlement Date */}
                <div>
                  <label className="cs-label block mb-1">
                    Settlement Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    <input
                      type="date"
                      value={formData.settlement_date}
                      onChange={(e) => setFormData({ ...formData, settlement_date: e.target.value })}
                      className="cs-body w-full pl-9 pr-4 py-2 rounded-lg"
                      style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                      required
                    />
                  </div>
                </div>

                {/* Settlement Type */}
                <div>
                  <label className="cs-label block mb-1">
                    Settlement Type *
                  </label>
                  <select
                    value={formData.settlement_type}
                    onChange={(e) => setFormData({ ...formData, settlement_type: e.target.value })}
                    className="cs-body w-full px-4 py-2 rounded-lg"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                  >
                    {settlementTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time to Resolution */}
                <div>
                  <label className="cs-label block mb-1">
                    Time to Resolution (days)
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    <input
                      type="number"
                      value={formData.time_to_resolution_days}
                      onChange={(e) => setFormData({ ...formData, time_to_resolution_days: e.target.value })}
                      className="cs-body w-full pl-9 pr-4 py-2 rounded-lg"
                      style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                {/* Satisfaction Rating */}
                <div>
                  <label className="cs-label block mb-1">
                    Satisfaction Rating
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          satisfaction_rating: formData.satisfaction_rating === rating.toString() ? '' : rating.toString()
                        })}
                        className="flex-1 py-2 rounded-lg transition"
                        style={
                          formData.satisfaction_rating === rating.toString()
                            ? { border: '1px solid var(--color-brand)', background: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
                            : { border: '1px solid var(--color-border)' }
                        }
                      >
                        <Star
                          className="w-5 h-5 mx-auto"
                          style={
                            formData.satisfaction_rating === rating.toString()
                              ? { fill: 'var(--color-brand)', color: 'var(--color-brand)' }
                              : { color: 'var(--color-text-dim)' }
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="cs-label block mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="cs-body w-full px-4 py-2 rounded-lg"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                    placeholder="Any additional notes about the outcome..."
                  />
                </div>
              </div>

              <div className="px-6 py-4 flex justify-end gap-3" style={{ background: 'var(--color-bg-base)', borderTop: '1px solid var(--color-border-sub)' }}>
                <button
                  onClick={() => setShowModal(false)}
                  className="cs-chip px-4 py-2 rounded-lg"
                  style={{ color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitOutcome}
                  disabled={submitting || !formData.final_settlement_amount || !formData.settlement_date}
                  className="cs-chip inline-flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)', border: '1px solid var(--color-brand)' }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Outcome
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </AppLayout>
  );
}
